import {
  useCustomerBusinessDetailsRetrieveSuspense,
  useCustomerCompanyRetrieveSuspense,
  useCustomerDebtScheduleListSuspense,
  useCustomerMajorityOwnerListSuspense,
} from "@boundless/bl-api-client/customer/services"

import { SummaryApplication } from "@/components/summary/SummaryApplication"
import { SummaryApplicationDownload } from "@/components/summary/SummaryApplicationDownload"
import { SummaryBankingConnection } from "@/components/summary/SummaryBankingConnection"
import { SummaryBusinessDetails } from "@/components/summary/SummaryBusinessDetails"
import { SummaryDebtTable } from "@/components/summary/SummaryDebtTable"
import { SummaryDocuments } from "@/components/summary/SummaryDocuments"
import { SummaryNavigation } from "@/components/summary/SummaryNavigation"
import { SummaryOwnersDetails } from "@/components/summary/SummaryOwnersDetails"
import { Accordion } from "@/components/ui/accordion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { needsDebtConfirmation } from "@/lib/utils"

import type { CustomerApplications } from "@boundless/bl-api-client/customer/models"

export type SummaryCardProps = {
  companyUuid: string
  application: CustomerApplications
  handleBack: () => void
  handleSubmit?: () => void
  isLoading?: boolean
  returnTo?: string
}

export function SummaryCard({
  companyUuid,
  application,
  handleBack,
  handleSubmit,
  isLoading,
  returnTo,
}: SummaryCardProps) {
  const { data: company } = useCustomerCompanyRetrieveSuspense(companyUuid)
  const { data: businessDetails } =
    useCustomerBusinessDetailsRetrieveSuspense(companyUuid)
  const { data: owners } = useCustomerMajorityOwnerListSuspense(companyUuid)
  const { data: debts } = useCustomerDebtScheduleListSuspense(companyUuid)
  const needsDebtReview = needsDebtConfirmation(company)

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl bg-gray-100 p-6">
          <Accordion
            type="single"
            collapsible
            defaultValue={needsDebtReview ? "debt-table" : undefined}
            className="space-y-2"
          >
            <SummaryBusinessDetails
              company={company}
              businessDetails={businessDetails}
              returnTo={returnTo || `/applications/${application.uuid}/summary`}
            />
            <SummaryOwnersDetails
              owners={owners}
              returnTo={returnTo || `/applications/${application.uuid}/summary`}
            />
            <SummaryBankingConnection
              company={company}
              returnTo={returnTo || `/applications/${application.uuid}/summary`}
            />
            {debts && (
              <SummaryDebtTable
                company={company}
                debts={debts}
                needsReview={needsDebtReview}
                returnTo={
                  returnTo || `/applications/${application.uuid}/summary`
                }
              />
            )}
            <SummaryApplication application={application} />
            <SummaryDocuments
              application={application}
              returnTo={returnTo || `/applications/${application.uuid}/summary`}
            />
          </Accordion>
        </div>
        <SummaryApplicationDownload application={application} />
        <SummaryNavigation
          application={application}
          handleBack={handleBack}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          needsDebtReview={needsDebtReview}
        />
      </CardContent>
    </Card>
  )
}
